// Cart store — single source of truth for the visitor's Wix cart on the client.
// Ported from the Noir Pétale / Sneakergang pattern (analytics + members
// stripped). Exposes a useCart() hook plus add/remove/quantity/checkout helpers
// and the shared open/closed state of the cart drawer.

'use client';

import { useSyncExternalStore } from 'react';
import { getWixBrowser } from './wix-browser';
import { wixMediaUrlClient } from './media';
import { trackAddToCart, trackInitiateCheckout } from './meta-pixel';

const WIX_STORES_APP_ID = '1380b703-ce81-ff05-f115-39571d94dfcd';
const REV_KEY = 'mc:cart-rev';
const CART_INITIALIZED_KEY = 'mc:cart-initialized';

/**
 * Free shipping threshold, in the store's currency; null = no free-shipping
 * promise anywhere on the site. Wix checkout currently charges a flat $15
 * domestic (no free-shipping rule), so set a number here only after the same
 * rule exists in Wix (Settings > Shipping), so the site never promises what
 * checkout doesn't do.
 */
export const FREE_SHIPPING_THRESHOLD: number | null = null;

function hasCartBeenInitialized(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return localStorage.getItem(CART_INITIALIZED_KEY) === '1';
  } catch {
    return false;
  }
}
function markCartInitialized(): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(CART_INITIALIZED_KEY, '1');
  } catch {}
}

/** A Wix descriptionLine — this is where the shopper's Size choice comes back. */
interface DescriptionLine {
  name?: { original?: string };
  plainText?: { original?: string };
  colorInfo?: { original?: string };
}

export interface CartLineItem {
  _id?: string;
  productName?: { original?: string };
  image?: string;
  price?: { amount?: string; formattedAmount?: string };
  quantity?: number;
  url?: { relativePath?: string };
  descriptionLines?: DescriptionLine[];
}

export interface CartSnapshot {
  status: 'loading' | 'ready' | 'empty' | 'error';
  lineItems: CartLineItem[];
  subtotal: string | null;
  /** Numeric subtotal, for the free-shipping progress bar. Null when unknown. */
  subtotalNumber: number | null;
  count: number;
}

const EMPTY: CartSnapshot = {
  status: 'loading',
  lineItems: [],
  subtotal: null,
  subtotalNumber: null,
  count: 0,
};

let snapshot: CartSnapshot = EMPTY;
const listeners = new Set<() => void>();
let inflight: Promise<void> | null = null;
let initialized = false;

/**
 * Monotonic mutation counter, and the guard against a stale read clobbering a
 * fresh write.
 *
 * Without it: a focus/storage event starts refresh(), which reads a two-item
 * cart from Wix. Before that resolves the shopper clicks Remove; the mutation
 * round-trips faster and sets a one-item snapshot. Then the older
 * getCurrentCart() resolves and setSnapshot() puts the removed line item back
 * on screen. The shopper sees the item they just deleted reappear, clicks
 * Remove again, and gets an error because it is already gone.
 *
 * Every mutation bumps `mutationSeq`. refresh() captures it before the request
 * and throws its own result away if it changed while the request was in the
 * air — the mutation's response is by definition the newer truth.
 */
let mutationSeq = 0;

function emit() {
  for (const fn of listeners) fn();
}
function setSnapshot(next: CartSnapshot) {
  snapshot = next;
  emit();
}
function bumpRev() {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(REV_KEY, String(Date.now()));
  } catch {}
}

interface RawCart {
  lineItems?: CartLineItem[];
  subtotal?: { amount?: string; formattedAmount?: string };
}

function normalize(raw: RawCart | null): CartSnapshot {
  const rawItems = raw?.lineItems ?? [];
  if (rawItems.length === 0) {
    return { status: 'empty', lineItems: [], subtotal: null, subtotalNumber: null, count: 0 };
  }
  const items = rawItems.map((li) => ({ ...li, image: wixMediaUrlClient(li.image) }));
  const count = items.reduce((sum, li) => sum + (li.quantity ?? 0), 0);
  const amount = Number(raw?.subtotal?.amount);
  return {
    status: 'ready',
    lineItems: items,
    subtotal: raw?.subtotal?.formattedAmount ?? null,
    subtotalNumber: Number.isFinite(amount) ? amount : null,
    count,
  };
}

/** Apply a mutation's own response, which is always newer than any read. */
function commitMutation(result: unknown): Promise<void> | void {
  mutationSeq += 1;
  const updated = (result as { cart?: RawCart })?.cart;
  if (updated) {
    setSnapshot(normalize(updated));
    bumpRev();
    return;
  }
  bumpRev();
  return refresh();
}

export async function refresh(): Promise<void> {
  if (inflight) return inflight;
  if (!hasCartBeenInitialized()) {
    setSnapshot({ status: 'empty', lineItems: [], subtotal: null, subtotalNumber: null, count: 0 });
    return;
  }
  const seenSeq = mutationSeq;
  inflight = (async () => {
    try {
      const wix = await getWixBrowser();
      const cart = await wix.currentCart.getCurrentCart();
      // A mutation landed while this read was in flight. Its response already
      // set a newer snapshot; this one is stale, so drop it on the floor.
      if (mutationSeq !== seenSeq) return;
      setSnapshot(normalize(cart as unknown as RawCart));
    } catch (err) {
      if (mutationSeq !== seenSeq) return;
      // A transport/server failure is NOT an empty cart. Collapsing the two is
      // how a shopper with items in the bag gets shown "Your cart is empty"
      // after a tab refocus hits a Wix blip — nothing was removed, but they see
      // an empty bag with no error and no retry, and they leave.
      //
      // Keep the last good snapshot and mark it errored so the UI can say so.
      console.warn('[cart] refresh failed; keeping last known cart', err);
      const last = getSnapshot();
      setSnapshot(
        last && last.status === 'ready'
          ? { ...last, status: 'error' }
          : { status: 'error', lineItems: [], subtotal: null, subtotalNumber: null, count: 0 },
      );
    } finally {
      inflight = null;
    }
  })();
  return inflight;
}

/**
 * Wix V1 accepts the shopper's option choice in one of TWO shapes, and which
 * one is correct depends on the product:
 *
 *   variant-managed (manageVariants true)  -> options: { variantId }
 *   option-only     (manageVariants false) -> options: { options: { Size: 'M' } }
 *
 * This store is entirely the second kind: all 110 products have
 * manageVariants false, 16 of them carry a real Size option (S/M/L), and every
 * product has exactly one variant whose id is the null UUID with empty choices.
 * Only the variantId shape was implemented, so a size could never be attached
 * to a line item and orders reached checkout with no size on them.
 *
 * Verified against the live Wix catalog before writing this: sending
 * `options: { options: { Size: 'S' } }` is accepted and comes back on the line
 * item as a descriptionLine reading Size / S.
 */
export async function addLineItem(opts: {
  productId: string;
  variantId?: string;
  /** Option name -> chosen value, for products that are not variant-managed. */
  choices?: Record<string, string>;
  quantity?: number;
}): Promise<void> {
  const wix = await getWixBrowser();
  const catalogReference: {
    catalogItemId: string;
    appId: string;
    options?: { variantId: string } | { options: Record<string, string> };
  } = { catalogItemId: opts.productId, appId: WIX_STORES_APP_ID };

  // variantId wins when present — a variant-managed product carries its choices
  // inside the variant, so sending both would be redundant and ambiguous.
  if (opts.variantId) {
    catalogReference.options = { variantId: opts.variantId };
  } else if (opts.choices && Object.keys(opts.choices).length > 0) {
    catalogReference.options = { options: opts.choices };
  }

  const result = await wix.currentCart.addToCurrentCart({
    lineItems: [{ catalogReference, quantity: opts.quantity ?? 1 }],
  });
  markCartInitialized();
  await commitMutation(result);
  trackAddToCart({ id: opts.productId, quantity: opts.quantity ?? 1 });
}

export async function removeLineItem(lineItemId: string): Promise<void> {
  const wix = await getWixBrowser();
  const result = await wix.currentCart.removeLineItemsFromCurrentCart([lineItemId]);
  await commitMutation(result);
}

/**
 * Empty the visitor's Wix cart (used by the Meta Shops /checkout landing so a
 * buyer never inherits a stale cart). deleteCurrentCart removes the cart
 * itself; the next addToCurrentCart creates a fresh one. A visitor with no
 * cart yet gets an error from Wix, which is the state we want, so it is
 * swallowed; any other failure falls back to removing the lines one by one.
 */
export async function clearCart(): Promise<void> {
  const wix = await getWixBrowser();
  try {
    await wix.currentCart.deleteCurrentCart();
  } catch {
    try {
      const cart = await wix.currentCart.getCurrentCart();
      const ids = (cart?.lineItems ?? []).map((l) => l._id).filter((id): id is string => Boolean(id));
      if (ids.length > 0) await wix.currentCart.removeLineItemsFromCurrentCart(ids);
    } catch {
      // No cart for this visitor: already empty.
    }
  }
  mutationSeq += 1;
  setSnapshot({ status: 'empty', lineItems: [], subtotal: null, subtotalNumber: null, count: 0 });
  bumpRev();
}

/**
 * Apply a coupon code to the current cart. Wix rejects an unknown, expired or
 * inapplicable code with an error, which becomes { ok: false }.
 */
export async function applyCoupon(code: string): Promise<{ ok: boolean }> {
  const wix = await getWixBrowser();
  try {
    const result = await wix.currentCart.updateCurrentCart({ couponCode: code });
    await commitMutation(result);
    const applied = (result as { cart?: { appliedDiscounts?: { coupon?: { code?: string } }[] } })?.cart
      ?.appliedDiscounts;
    return { ok: (applied ?? []).some((d) => d.coupon?.code?.toLowerCase() === code.toLowerCase()) };
  } catch (err) {
    console.warn('[cart] coupon rejected', err);
    return { ok: false };
  }
}

/**
 * Set a line item's quantity. Wix clamps to remaining stock and returns the
 * clamped value, so the response — not the requested number — is what we show.
 * Quantity 0 is not a valid update; callers route that to removeLineItem.
 */
export async function setLineItemQuantity(
  lineItemId: string,
  quantity: number,
): Promise<number | undefined> {
  if (quantity < 1) {
    await removeLineItem(lineItemId);
    return undefined;
  }
  const wix = await getWixBrowser();
  const result = await wix.currentCart.updateCurrentCartLineItemQuantity([
    { _id: lineItemId, quantity },
  ]);
  // The quantity Wix actually kept (it clamps to stock), so the UI can say so.
  const applied = (result as { cart?: RawCart })?.cart?.lineItems?.find((l) => l._id === lineItemId)
    ?.quantity;
  await commitMutation(result);
  return applied;
}

/**
 * Create a Wix checkout from the current cart and return the hosted-checkout
 * URL to redirect to. NOTE: Wix's hosted checkout page is not brandable — the
 * customer leaves our pixel-matched UI for Wix's payment screen here.
 */
export async function startCheckout(): Promise<string> {
  const wix = await getWixBrowser();
  const checkoutRes = await wix.currentCart.createCheckoutFromCurrentCart({
    channelType: 'WEB' as never,
  });
  const checkoutId = (checkoutRes as unknown as { checkoutId?: string }).checkoutId;
  if (!checkoutId) throw new Error('Wix did not return a checkoutId');

  const origin = window.location.origin;
  const redirectRes = await wix.redirects.createRedirectSession({
    ecomCheckout: { checkoutId },
    callbacks: { postFlowUrl: `${origin}/cart`, thankYouPageUrl: `${origin}/` },
  });
  const fullUrl = (redirectRes as unknown as { redirectSession?: { fullUrl?: string } })
    .redirectSession?.fullUrl;
  if (!fullUrl) throw new Error('Wix did not return a redirect URL');

  trackInitiateCheckout({ value: snapshot.subtotalNumber, numItems: snapshot.count });

  const customHost = process.env.NEXT_PUBLIC_WIX_CHECKOUT_HOST;
  return customHost
    ? fullUrl.replace(/https:\/\/[^/]+\/([^/]+\/)?/, `https://${customHost}/`)
    : fullUrl;
}

/** Human-readable option choices on a line item, e.g. ["Size: M"]. */
export function lineItemChoices(li: CartLineItem): string[] {
  return (li.descriptionLines ?? [])
    .map((d) => {
      const name = d.name?.original;
      const value = d.plainText?.original ?? d.colorInfo?.original;
      if (!value) return null;
      return name ? `${name}: ${value}` : value;
    })
    .filter((s): s is string => Boolean(s));
}

function init() {
  if (initialized || typeof window === 'undefined') return;
  initialized = true;
  refresh();
  window.addEventListener('storage', (e) => {
    if (e.key === REV_KEY) refresh();
  });
  window.addEventListener('focus', () => refresh());
}

function subscribe(fn: () => void): () => void {
  init();
  listeners.add(fn);
  return () => listeners.delete(fn);
}
function getSnapshot(): CartSnapshot {
  return snapshot;
}
/** Non-hook read of the current snapshot, for one-off flows like the Meta checkout landing. */
export function getCartSnapshot(): CartSnapshot {
  return snapshot;
}
function getServerSnapshot(): CartSnapshot {
  return EMPTY;
}

export function useCart(): CartSnapshot {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}

// ---------------------------------------------------------------------------
// Cart drawer open/closed — shared state, so "Add to Cart" anywhere on the page
// can slide the drawer open without the grid and the header having to know
// about each other.
// ---------------------------------------------------------------------------

let drawerOpen = false;
const drawerListeners = new Set<() => void>();

// The element focused when the drawer opened, so focus can go back to it on
// close. Captured here (not in the drawer's effect) because by the time the
// effect runs the opener may already be disabled or unmounted.
let drawerOpener: HTMLElement | null = null;

export function getCartDrawerOpener(): HTMLElement | null {
  return drawerOpener?.isConnected ? drawerOpener : null;
}

export function openCartDrawer(): void {
  if (drawerOpen) return;
  const active = typeof document !== 'undefined' ? document.activeElement : null;
  drawerOpener = active instanceof HTMLElement && active !== document.body ? active : null;
  drawerOpen = true;
  for (const fn of drawerListeners) fn();
}
export function closeCartDrawer(): void {
  if (!drawerOpen) return;
  drawerOpen = false;
  for (const fn of drawerListeners) fn();
}

function subscribeDrawer(fn: () => void): () => void {
  drawerListeners.add(fn);
  return () => drawerListeners.delete(fn);
}
function getDrawerSnapshot(): boolean {
  return drawerOpen;
}
function getDrawerServerSnapshot(): boolean {
  return false;
}

export function useCartDrawer(): boolean {
  return useSyncExternalStore(subscribeDrawer, getDrawerSnapshot, getDrawerServerSnapshot);
}
